"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ReplayPlayer = void 0;
const gl_matrix_1 = require("gl-matrix");
const nanoevents_1 = require("nanoevents");
const ReplayState_1 = require("./Replay/ReplayState");
const ReplayCustomMap_1 = require("./Replay/ReplayCustomMap");
const IN_JUMP = 1 << 0;
const IN_DUCK = 1 << 1;
const IN_FORWARD = 1 << 2;
const IN_BACK = 1 << 3;
const IN_MOVELEFT = 1 << 4;
const IN_MOVERIGHT = 1 << 5;
const REPLAY_EYE_HEIGHT = 28;
const REPLAY_DUCK_EYE_HEIGHT = 12;
const updateGame = (game, state, ducking = false) => {
    game.camera.position[0] = state.cameraPos[0];
    game.camera.position[1] = state.cameraPos[1];
    game.camera.position[2] = state.cameraPos[2] + (ducking ? REPLAY_DUCK_EYE_HEIGHT : REPLAY_EYE_HEIGHT);
    game.camera.rotation[0] = gl_matrix_1.glMatrix.toRadian(state.cameraRot[0]);
    game.camera.rotation[1] = gl_matrix_1.glMatrix.toRadian(state.cameraRot[1]);
    game.camera.rotation[2] = gl_matrix_1.glMatrix.toRadian(state.cameraRot[2]);
};
class ReplayPlayer {
    constructor(game) {
        this.oldButtons = 0;
        this.oldGravity = false;
        this.prevFrame = 0;
        this.nextFrame = 0;
        this.timeOffset = 0;
        this.frameTime = 0;
        this.currentFrame = 0;
        this.currentTime = 0;
        this.isPlaying = false;
        this.isPaused = false;
        this.speed = 1;
        this.tmpVecA = new ReplayCustomMap_1.Vector3([0, 0, 0]);
        this.tmpVecB = new ReplayCustomMap_1.Vector3([0, 0, 0]);
        this.reset();
        this.game = game;
        this.state = new ReplayState_1.ReplayState();
        this.replay = null;
        this.events = (0, nanoevents_1.createNanoEvents)();
    }
    msToFullTime(ms) {
        const seconds = Math.floor(ms / 1000);
        const milliseconds = ms % 1000;
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;
        const mm = String(minutes).padStart(2, '0');
        const ss = String(remainingSeconds).padStart(2, '0');
        const msStr = String(milliseconds).padStart(3, '0');
        return `${mm}:${ss}.${msStr}`;
    }
    reset() {
        this.nextFrame = 0;
        this.prevFrame = 0;
        this.frameTime = 0;
        this.currentFrame = 0;
        this.timeOffset = 0;
        this.currentTime = 0;
        this.isPlaying = false;
        this.isPaused = false;
        this.speed = 1;
        if (this.replay) {
            let firstFrame = this.replay.data.frames[0];
            let state = new ReplayState_1.ReplayState();
            state.cameraPos = firstFrame.origin;
            state.cameraRot = firstFrame.rotation;
            this.state = state;
            this.frameTime = (this.replay.data.time / this.replay.data.frames.length) / 1000;
        }
    }
    changeReplay(replay) {
        this.replay = replay;
        this.reset();
    }
    applyCamera() {
        updateGame(this.game, this.state, this.isDucking());
    }
    focusCameraOnCurrentFrame() {
        var _a, _b;
        const frames = (_b = (_a = this.replay) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.frames;
        if (!frames || frames.length === 0)
            return;
        const frame = frames[Math.min(this.currentFrame, frames.length - 1)];
        this.state.cameraPos = frame.origin;
        this.state.cameraRot = [frame.rotation[0], frame.rotation[1], 0];
        updateGame(this.game, this.state, this.isDucking());
    }
    isDucking() {
        var _a, _b, _c;
        const frame = (_c = (_b = (_a = this.replay) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.frames) === null || _c === void 0 ? void 0 : _c[this.currentFrame];
        return !!frame && (frame.buttons & IN_DUCK) !== 0;
    }
    play() {
        if (!this.isPlaying) {
            this.isPlaying = true;
        }
        else if (this.isPaused) {
            this.isPaused = false;
        }
        this.events.emit('play');
    }
    pause() {
        if (this.isPlaying) {
            this.isPaused = true;
        }
        this.events.emit('pause');
    }
    stop() {
        this.reset();
        this.events.emit('stop');
    }
    speedUp() {
        this.speed = Math.min(this.speed * 2, 1);
    }
    speedDown() {
        this.speed = Math.max(this.speed / 2, 0.12);
    }
    finish(updateCamera) {
        const frames = this.replay.data.frames;
        this.currentFrame = frames.length - 1;
        this.prevFrame = this.currentFrame;
        this.nextFrame = this.currentFrame;
        this.currentTime = this.replay.data.time / 1000;
        this.timeOffset = 0;
        this.state.cameraPos = frames[this.currentFrame].origin;
        this.state.cameraRot = frames[this.currentFrame].rotation;
        if (updateCamera) {
            updateGame(this.game, this.state, this.isDucking());
        }
        this.pause();
    }
    seek(dt, updateCamera = true, updateStats = true) {
        if (!this.replay || this.replay.data.frames.length === 0) {
            return;
        }
        if (this.currentFrame >= this.replay.data.frames.length - 1) {
            this.finish(updateCamera);
            return;
        }
        dt *= this.speed;
        const duration = this.replay.data.time / 1000;
        this.currentTime = duration > 0
            ? Math.min(this.currentTime + dt, duration)
            : this.currentTime + dt;
        if (duration > 0 && this.currentTime >= duration) {
            this.finish(updateCamera);
            return;
        }
        const frameDuration = this.frameTime;
        this.timeOffset += dt;
        if (this.timeOffset >= frameDuration) {
            this.timeOffset = this.timeOffset - frameDuration;
            this.prevFrame = this.currentFrame;
            this.nextFrame = this.currentFrame + 1;
            const currentFrameData = this.replay.data.frames[this.currentFrame];
            const { timestamp, speed, fps, buttons, strafes, sync, gravity } = currentFrameData;
            if (timestamp > 0)
                this.frameTime = timestamp / 1000.0;
            if (updateStats) {
                this.game.setSpeed(speed.toString());
                this.game.setFps(fps.toString());
                if (gravity !== this.oldGravity) {
                    this.oldGravity = gravity;
                    this.game.setGravity(gravity);
                }
                if (buttons !== this.oldButtons) {
                    this.oldButtons = buttons;
                    const btnString = this.formatKeys(buttons);
                    this.game.setButtons(btnString);
                }
                if (strafes)
                    this.game.setStrafes(`Strafes: ${strafes}\nSync: ${sync}`);
            }
            this.currentFrame++;
        }
        if (this.currentFrame > 0) {
            const t = this.timeOffset / frameDuration > 1.0 ? 1.0 : this.timeOffset / frameDuration;
            const prevFrameData = this.replay.data.frames[this.prevFrame];
            const nextFrameData = this.replay.data.frames[this.nextFrame];
            this.tmpVecA.setFrom(prevFrameData.origin);
            this.tmpVecB.setFrom(nextFrameData.origin);
            this.tmpVecA.lerpTo(this.tmpVecB, t);
            const interpolatedRot = [0, 0, 0];
            interpolatedRot[0] = this.slerp(prevFrameData.rotation[0], nextFrameData.rotation[0], t);
            interpolatedRot[1] = this.slerp(prevFrameData.rotation[1], nextFrameData.rotation[1], t);
            this.state.cameraPos = this.tmpVecA.vec;
            this.state.cameraRot = interpolatedRot;
            if (updateCamera) {
                updateGame(this.game, this.state, this.isDucking());
            }
        }
    }
    formatKeys(buttons) {
        return `${(buttons & IN_FORWARD ? "W" : "    ")}    ${(buttons & IN_JUMP ? "JUMP" : "         ")}\n` +
            `${(buttons & IN_MOVELEFT ? "A" : "    ")}${(buttons & IN_BACK ? "S" : "    ")}${(buttons & IN_MOVERIGHT ? "D" : "    ")}    ${(buttons & IN_DUCK ? "DUCK" : "         ")}\n`;
    }
    slerp(a, b, dt) {
        a = a % 360;
        b = b % 360;
        const dist = this.getDist(a, b);
        if (Math.abs(a - b) > 180) {
            if (a < b) {
                return (a + 180 - dist * dt + 360) % 360 - 180;
            }
            else {
                return (a + 180 + dist * dt + 360) % 360 - 180;
            }
        }
        else {
            if (a < b) {
                return (a + 180 + dist * dt + 360) % 360 - 180;
            }
            else {
                return (a + 180 - dist * dt + 360) % 360 - 180;
            }
        }
    }
    getDist(a, b) {
        const rawDiff = Math.abs(a - b);
        return 180.0 - Math.abs(rawDiff - 180.0);
    }
    seekByPercent(value, updateCamera = true) {
        if (!this.replay || this.replay.data.frames.length === 0) {
            return;
        }
        value = Math.max(0, Math.min(value, 100)) / 100;
        this.currentTime = this.replay.data.time * value / 1000;
        this.currentFrame = Math.min(this.replay.data.frames.length - 1, Math.round((this.replay.data.frames.length - 1) * value));
        this.prevFrame = this.currentFrame;
        this.nextFrame = this.currentFrame;
        this.timeOffset = 0;
        this.state.cameraPos = this.replay.data.frames[this.currentFrame].origin;
        this.state.cameraRot = this.replay.data.frames[this.currentFrame].rotation;
        if (updateCamera) {
            updateGame(this.game, this.state, this.isDucking());
        }
    }
    update(dt, updateCamera = true, updateStats = true) {
        if (!this.isPlaying || this.isPaused) {
            return;
        }
        this.seek(dt, updateCamera, updateStats);
        let hitStop = false;
        if (hitStop) {
            this.stop();
        }
    }
}
exports.ReplayPlayer = ReplayPlayer;
